import { AuthAvailableTournament } from 'modules/auth/domain/interfaces/auth-available-tournament';
import { ListAvailableTournamentsArgs, useListAvailableTournamentsQuery } from 'modules/auth/store/available-tournaments-api';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { useEffect, useRef, useState } from 'react';
import { useOffsetPagination } from 'shared/hooks/use-offset-pagination';

/** Не отправляем на бэк слишком короткий поисковый запрос */
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;
const TOURNAMENTS_PER_PAGE = 6;

const filtersConfig = {
  filter: { key: 'filter', defaultValue: '' as string },
};

const mapFiltersToArgs = (filters: { filter: string }) => {
  const namePart = filters.filter.trim();
  return { namePart: namePart.length >= MIN_SEARCH_LENGTH ? namePart : undefined };
};

export function useAvailableTournaments() {
  const { user, isChecking } = useAuth();

  const { items, isLoading, isError, page, totalPages, filters, setPage, setFilters } = useOffsetPagination<
    AuthAvailableTournament,
    ListAvailableTournamentsArgs,
    typeof filtersConfig
  >({
    useQueryHook: useListAvailableTournamentsQuery,
    mapFiltersToArgs,
    filtersConfig,
    perPageStorageKey: 'availableTournamentsPerPage',
    defaultPerPage: TOURNAMENTS_PER_PAGE,
    queryOptions: () => ({ skip: !user?.id }),
  });

  // Инпут обновляется мгновенно, в URL (и, следовательно, в запрос) значение
  // уезжает с задержкой. Дебаунс на эффекте, а не на debounce(): setFilters
  // пересоздаётся при каждом изменении searchParams и сбрасывал бы таймер,
  // плюс эффект сам чистит таймер при размонтировании.
  const [search, setSearch] = useState(filters.filter);
  const setFiltersRef = useRef(setFilters);
  setFiltersRef.current = setFilters;

  useEffect(() => {
    if (search === filters.filter) {
      return;
    }

    // Пустая строка → undefined, чтобы параметр вычищался из URL, а не висел как ?filter=
    const timer = setTimeout(() => setFiltersRef.current({ filter: search || undefined }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, filters.filter]);

  return {
    data: items,
    isLoading: isLoading || isChecking,
    isError,
    search,
    setSearch,
    page,
    pages: totalPages,
    setPage,
  };
}
